import React from 'react';
import { Home, Zap, Battery, Building2, Network, Wrench } from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  Home,
  Zap,
  Battery,
  Building2,
  Network,
  Wrench
};

const ServiceCard = ({ service, index }) => {
  const Icon = iconMap[service.icon];
  
  return (
    <div 
      className="group relative bg-neutral-900/50 border border-white/5 p-8 hover:border-electric-yellow/30 transition-all duration-500 overflow-hidden"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      {/* Electric pulse effect on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-electric-yellow to-transparent animate-electric-horizontal" />
        <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-electric-yellow to-transparent animate-electric-horizontal-reverse" />
      </div>
      
      {/* Corner accent */}
      <div className="absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-electric-yellow/0 group-hover:border-electric-yellow transition-all duration-500" />
      <div className="absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 border-electric-yellow/0 group-hover:border-electric-yellow transition-all duration-500" />
      
      {/* Icon with zap effect */}
      <div className="relative w-14 h-14 flex items-center justify-center bg-electric-yellow/10 mb-6 group-hover:bg-electric-yellow transition-all duration-400">
        <Icon 
          size={28} 
          className="text-electric-yellow group-hover:text-black transition-colors duration-400 group-hover:animate-zap"
        />
        {/* Spark particles */}
        <div className="absolute -top-1 -right-1 w-2 h-2 bg-electric-yellow rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-spark-1" />
        <div className="absolute -bottom-1 -left-1 w-1.5 h-1.5 bg-electric-yellow rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-spark-2" />
      </div>
      
      {/* Content */}
      <h3 className="text-xl font-semibold text-white mb-3">
        {service.title}
      </h3>
      <p className="text-neutral-400 leading-relaxed">
        {service.description}
      </p>
    </div>
  );
};

const ServicesSection = () => {
  return (
    <section id="services" className="bg-black py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
        {/* Section header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-electric-yellow" />
              <span className="text-electric-yellow text-sm font-medium tracking-widest uppercase">
                Our Expertise
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Comprehensive Electrical<br />
              <span className="text-neutral-500">Solutions</span>
            </h2>
          </div>
          <p className="text-neutral-400 max-w-md text-lg">
            From residential upgrades to large-scale commercial projects, 
            we deliver precision electrical work across Canada.
          </p>
        </div>

        {/* Residential Services */}
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-8">
            <div className="px-4 py-2 bg-electric-yellow/10 border border-electric-yellow/30">
              <span className="text-electric-yellow text-sm font-medium">Residential</span>
            </div>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.residential.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
        </div>

        {/* Commercial Services */}
        <div>
          <div className="flex items-center gap-4 mb-8">
            <div className="px-4 py-2 bg-electric-yellow/10 border border-electric-yellow/30">
              <span className="text-electric-yellow text-sm font-medium">Commercial</span>
            </div>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.commercial.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index + 3} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
